import * as THREE from 'three';
import Engine from './Engine';
import ScopeCamera from './Camera';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import TransformUtils from './utilities/TransformUtils';
import { BillboardModes } from '../interfaces/enums';
import { CustomEventTypes, defaultBillboardObject } from '../globals';
import { BillboardSettings } from '../interfaces/Billboard';

interface BillboardingData
{
	object: THREE.Object3D;
	target: THREE.Object3D | null;
	initialRotation: THREE.Euler;
	initialPosition: THREE.Vector3;
	centerPointOffset: THREE.Vector3;
}

export default class BillboardManager 
{
	private sceneToBillboardedObjectGuids: Map<string, string[]> = new Map<string, string[]>();
	private objectGuidToBillboardedDataMap: Map<string, BillboardingData> = new Map<string, BillboardingData>();

	private upVector = new THREE.Vector3(0, 1, 0);
	private yFlipQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI, 0));
	private lookAtMatrix = new THREE.Matrix4();
	private lookAtQuat = new THREE.Quaternion();
	private lookAtEuler = new THREE.Euler();
	private offsetEuler = new THREE.Euler();
	private offsetQuat = new THREE.Quaternion();
	private worldPosition = new THREE.Vector3();
	private targetWorldPosition = new THREE.Vector3();
	private centerPointDummyObject = new THREE.Object3D();

	constructor() 
	{
		document.addEventListener(CustomEventTypes.simulationStarted, this.onSimulationStarted.bind(this));
		document.addEventListener(CustomEventTypes.simulationStopped, this.onSimulationStopped.bind(this));

		this.centerPointDummyObject.userData.hideInHierarchy = true;
		this.centerPointDummyObject.userData.ignoreExport = true;
	}

	private onSimulationStarted(e: Event)
	{
		this.sceneToBillboardedObjectGuids.forEach((objectGuids, sceneId) => 
		{
			for (const guid of objectGuids) 
			{
				const billboardingData = this.objectGuidToBillboardedDataMap.get(guid);
				if(!billboardingData) continue;
				const object = billboardingData.object;
				if (object.userData.billboard.mode === BillboardModes.LookAt)
				{
					if(object.userData.billboard.targetGuid && object.userData.billboard.targetGuid !== object.userData.guid) 
					{
						billboardingData.target = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(sceneId), object.userData.billboard.targetGuid);
					}
					else
					{
						billboardingData.target = null;
					}
				}
			
				billboardingData.initialRotation.copy(object.rotation);
				billboardingData.initialPosition.copy(object.position);
				if(object instanceof THREE.Mesh && object.geometry)
				{
					if (!object.geometry.boundingBox) object.geometry.computeBoundingBox();
					object.geometry.boundingBox.getCenter(billboardingData.centerPointOffset);
				}
			}
		});
	}

	private onSimulationStopped(e: Event)
	{
		this.sceneToBillboardedObjectGuids.forEach((objectGuids, sceneId) => 
		{
			for (const guid of objectGuids) 
			{
				const billboardingData = this.objectGuidToBillboardedDataMap.get(guid);
				if(!billboardingData) continue;
				const object = billboardingData.object;
				object.rotation.copy(billboardingData.initialRotation);
				object.position.copy(billboardingData.initialPosition);
			}
		});
	}

	clearData() : void
	{
		this.sceneToBillboardedObjectGuids.clear();
		this.objectGuidToBillboardedDataMap.clear();
	}

	enableBillboard(object: THREE.Object3D, sceneId: string = Engine.getInstance().getCurrentScene()?.userData.guid) : void
	{
		if (!this.sceneToBillboardedObjectGuids.has(sceneId))
		{
			this.sceneToBillboardedObjectGuids.set(sceneId, []);
		} 

		if (!object.userData.billboard)
		{
			object.userData.billboard = JSON.parse(JSON.stringify(defaultBillboardObject));
		} 

		object.userData.billboard.enabled = true;

		if (!this.objectGuidToBillboardedDataMap.has(object.userData.guid))
		{
			this.objectGuidToBillboardedDataMap.set(object.userData.guid, {
				object: object,
				target: null,
				initialRotation: new THREE.Euler(),
				initialPosition: new THREE.Vector3(),
				centerPointOffset: new THREE.Vector3()
			});

			this.sceneToBillboardedObjectGuids.get(sceneId)!.push(object.userData.guid);
		}
	}

	disableBillboard(object: THREE.Object3D, sceneId: string = Engine.getInstance().getCurrentScene()?.userData.guid) : void
	{
		if(!object.userData.billboard) return;
		object.userData.billboard.enabled = false;
		if(this.sceneToBillboardedObjectGuids.has(sceneId))
		{
			const billboardIndex = this.sceneToBillboardedObjectGuids.get(sceneId)!.indexOf(object.userData.guid);
			if (billboardIndex > -1)
			{
				this.sceneToBillboardedObjectGuids.get(sceneId)!.splice(billboardIndex, 1);
			}
		}
	}

	getBillboardRotation(object: THREE.Object3D, target: THREE.Object3D, billboardSettings: BillboardSettings) : THREE.Quaternion
	{
		object.getWorldPosition(this.worldPosition);
		target.getWorldPosition(this.targetWorldPosition);
		this.lookAtQuat.setFromRotationMatrix(this.lookAtMatrix.lookAt(this.worldPosition, this.targetWorldPosition, this.upVector));
		if (!(object instanceof ScopeCamera)) this.lookAtQuat.multiply(this.yFlipQuat);

		if (billboardSettings.lockX) 
		{
			this.lookAtEuler.setFromQuaternion(this.lookAtQuat, "YXZ");
			this.lookAtEuler.set(0, this.lookAtEuler.y, this.lookAtEuler.z, "YXZ");
			this.lookAtQuat.setFromEuler(this.lookAtEuler);
		}
		if (billboardSettings.lockY) 
		{
			this.lookAtEuler.setFromQuaternion(this.lookAtQuat, "YXZ");
			this.lookAtEuler.set(this.lookAtEuler.x, 0, this.lookAtEuler.z, "YXZ");
			this.lookAtQuat.setFromEuler(this.lookAtEuler);
		}
		if (billboardSettings.lockZ) 
		{
			this.lookAtEuler.setFromQuaternion(this.lookAtQuat, "YXZ");
			this.lookAtEuler.set(this.lookAtEuler.x, this.lookAtEuler.y, 0, "YXZ");
			this.lookAtQuat.setFromEuler(this.lookAtEuler);
		}
		this.offsetEuler.set(billboardSettings.offsetRotation.x / 180.0 * Math.PI, billboardSettings.offsetRotation.y / 180.0 * Math.PI,	billboardSettings.offsetRotation.z / 180.0 * Math.PI);
		this.offsetQuat.setFromEuler(this.offsetEuler);
		this.lookAtQuat.multiply(this.offsetQuat);
		return this.lookAtQuat;
	}

	update() : void
	{
		const sceneId = Engine.getInstance().getCurrentScene()?.userData.guid;
		if (!this.sceneToBillboardedObjectGuids.has(sceneId)) return;

		const billboardedObjectGuids = this.sceneToBillboardedObjectGuids.get(sceneId)!;
		for (const guid of billboardedObjectGuids) 
		{
			const billboardingData = this.objectGuidToBillboardedDataMap.get(guid);
			if(!billboardingData) continue;
			const object = billboardingData.object;
			if (object.userData.removedFromScene) continue;
			const billboardSettings = object.userData.billboard as BillboardSettings;
			const target = billboardSettings.mode === BillboardModes.LookAt ? billboardingData.target : Engine.getInstance().getCamera();
			if (!target || target.userData.removedFromScene) continue;

			if (billboardSettings.forceRotateAboutCenter && object instanceof THREE.Mesh && object.geometry)
			{
				const parent = object.parent!;
				const centerOffset = billboardingData.centerPointOffset;
				parent.attach(this.centerPointDummyObject);
				object.getWorldPosition(this.worldPosition);
				TransformUtils.setWorldPosition(this.centerPointDummyObject, this.worldPosition.add(new THREE.Vector3().multiplyVectors(centerOffset, (object.scale))));
				this.centerPointDummyObject.attach(object);
				object.rotation.set(0, 0, 0);
				TransformUtils.setWorldQuaternion(this.centerPointDummyObject, this.getBillboardRotation(this.centerPointDummyObject, target, billboardSettings));
				parent.attach(object);
			}
			else 
			{
				TransformUtils.setWorldQuaternion(object, this.getBillboardRotation(object, target, billboardSettings));
			}
		}
	}
}

